import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Trash2, Plus, Edit } from "lucide-react";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function Settings() {
  const [admins, setAdmins] = useState([]);
  
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [roleToCreate, setRoleToCreate] = useState("admin");
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [editUsername, setEditUsername] = useState("");
  const [editPassword, setEditPassword] = useState("");

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchAdmins = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/users/admins`, { withCredentials: true });
      setAdmins(data);
    } catch (e) {
      toast.error("Yöneticiler alınamadı.");
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newUsername.trim() || !newPassword.trim()) return;
    try {
      await axios.post(`${BACKEND_URL}/api/users/admins`, { username: newUsername, password: newPassword, role: roleToCreate }, { withCredentials: true });
      toast.success(roleToCreate === "assistant" ? "Yardımcı yönetici oluşturuldu." : "Yeni yönetici oluşturuldu.");
      setCreateModalOpen(false);
      setNewUsername("");
      setNewPassword("");
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Oluşturulamadı.");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editUsername.trim()) return;
    try {
      const payload = { username: editUsername };
      if (editPassword.trim()) {
        payload.password = editPassword;
      }
      
      await axios.put(`${BACKEND_URL}/api/users/admins/${selectedAdmin.id}`, payload, { withCredentials: true });
      toast.success("Kullanıcı bilgileri güncellendi.");
      setEditModalOpen(false);
      setSelectedAdmin(null);
      setEditPassword("");
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Güncellenemedi.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/users/admins/${id}`, { withCredentials: true });
      toast.success("Kullanıcı silindi.");
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi.");
    }
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-border pb-4 gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Kullanıcı Ayarları</h1>
          <p className="text-sm text-muted-foreground mt-1">Yönetici hesaplarını ekleyebilir, silebilir veya bilgilerinizi güncelleyebilirsiniz.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { setRoleToCreate("assistant"); setCreateModalOpen(true); }} variant="outline" data-testid="open-create-assistant-modal">
            <Plus size={16} className="mr-2" /> Yeni Yardımcı Yönetici
          </Button>
          <Button onClick={() => { setRoleToCreate("admin"); setCreateModalOpen(true); }} data-testid="open-create-admin-modal">
            <Plus size={16} className="mr-2" /> Yeni Yönetici Ekle
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {admins.map(admin => (
          <Card key={admin.id} className="swiss-card flex items-center justify-between p-4 border-l-4 border-l-primary">
            <div className="flex flex-col">
              <span className="font-medium" data-testid={`admin-name-${admin.id}`}>{admin.username}</span>
              <span className="text-xs text-muted-foreground uppercase tracking-widest mt-0.5">
                {admin.role === "assistant" ? "Yardımcı Yönetici" : "Yönetici (Admin)"}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-primary hover:bg-primary/10"
                onClick={() => {
                  setSelectedAdmin(admin);
                  setEditUsername(admin.username);
                  setEditPassword("");
                  setEditModalOpen(true);
                }}
                data-testid={`edit-admin-${admin.id}`}
              >
                <Edit size={18} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive hover:bg-destructive/10"
                onClick={() => {
                  toast("Emin misiniz?", {
                    action: {
                      label: "Evet, Sil",
                      onClick: () => handleDelete(admin.id)
                    }
                  });
                }}
                data-testid={`delete-admin-${admin.id}`}
              >
                <Trash2 size={18} />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Create Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{roleToCreate === "assistant" ? "Yeni Yardımcı Yönetici Ekle" : "Yeni Yönetici Ekle"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Kullanıcı Adı</Label>
              <Input value={newUsername} onChange={e => setNewUsername(e.target.value)} required data-testid="new-admin-username" />
            </div>
            <div className="space-y-2">
              <Label>Şifre</Label>
              <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required data-testid="new-admin-password" />
            </div>
            <Button type="submit" className="w-full h-10 mt-2" data-testid="save-new-admin-btn">Ekle</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Kullanıcı Bilgilerini Güncelle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Yeni Kullanıcı Adı</Label>
              <Input value={editUsername} onChange={e => setEditUsername(e.target.value)} required data-testid="edit-admin-username" />
            </div>
            <div className="space-y-2">
              <Label>Yeni Şifre (Değiştirmek istemiyorsanız boş bırakın)</Label>
              <Input type="password" placeholder="••••••••" value={editPassword} onChange={e => setEditPassword(e.target.value)} data-testid="edit-admin-password" />
            </div>
            <Button type="submit" className="w-full h-10 mt-2" data-testid="save-edit-admin-btn">Kaydet</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
